import React, { useMemo, useState } from "react";
import ThreeColumnLayout from "@/components/common/three-column-layout";
import ThreadList from "./thread-list";
import { v4 as uuid } from "uuid";
import { ConfirmationModalBase } from "@/components/common/modals";
import FullModal from "../../full-modal";
import type { ThreadConfig } from "./types";
import { useUIStore } from "@/workflow_api/store/uiStore";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import type { NodeData, RefName } from "@/workflow_api/types/workflow";
import { NodeTypes } from "@/workflow_api/types/workflow";
import { useUpdateNode } from "@/workflow_api/services/nodes.api";
import { useNodeNameValidation } from "@/workflow_api/hooks/useNodeNameValidation";
import { deserializeMultiThreadNode } from "./serialization";
import {
  deserializeWaitInputs,
  serializeWaitInputs,
  serializeWaitEdges,
  type WaitInput,
} from "../wait/serialization";
import Variables from "@/workflow_api/components/variables";
import Empty from "@/workflow_api/components/variables/empty";

interface Props {
  initialTitle?: string;
}

export const MultiThreadDetails: React.FC<Props> = ({
  initialTitle = "Multihilos",
}) => {
  const {
    selectedNodeId,
    nodeDetailsOpen,
    setNodeDetailsOpen,
    setSelectedNodeId,
  } = useUIStore();
  const { getNodeById, flowId, nodes, edges } = useWorkflowStore();
  const [modalState, setModalState] = useState<"idle" | "close-warning">(
    "idle",
  );
  const [showNodeTitleValidation, setShowNodeTitleValidation] = useState(false);
  const { getNameValidationError, isNameValid } =
    useNodeNameValidation(selectedNodeId);

  const node = getNodeById(selectedNodeId || "");
  const nodeData = node?.data as unknown as NodeData;

  const [title, setTitle] = useState(nodeData?.displayName || initialTitle);
  const [manualConfirmation, setManualConfirmation] = useState(() => {
    return nodeData?.manual_continue ?? false;
  });

  // Deserialize existing threads from inputs, or fall back to connections
  const existingThreads = useMemo(() => {
    if (nodeData?.inputs && nodeData.inputs.length > 0) {
      return deserializeMultiThreadNode(nodeData.inputs);
    }

    if (nodeData?.connections) {
      const connectionEntries = Object.entries(
        nodeData.connections as Record<
          string,
          { display_name?: string; target_node?: string | null }
        >,
      )
        .filter(([key]) => key.startsWith("thread_"))
        .sort((a, b) => {
          const ai = parseInt(a[0].split("_")[1] || "0", 10);
          const bi = parseInt(b[0].split("_")[1] || "0", 10);
          return ai - bi;
        })
        .map(([, value]) => ({
          id: uuid(),
          label: (value?.display_name as string) || "",
        }));

      if (connectionEntries.length > 0) return connectionEntries;
    }

    return null;
  }, [nodeData?.inputs, nodeData?.connections]);

  const [threads, setThreads] = useState<ThreadConfig[]>(() => {
    if (existingThreads) {
      return existingThreads;
    }
    // Default 2 threads
    return Array.from({ length: 2 }, () => ({
      id: uuid(),
      label: "",
    }));
  });

  const onAddThread = () => {
    setThreads((prev) => [
      ...prev,
      {
        id: uuid(),
        label: `Salida ${prev.length + 1}`,
      },
    ]);
  };

  const onDeleteThread = (id: string) => {
    setThreads((prev) => prev.filter((t) => t.id !== id));
  };

  const onUpdateThread = (id: string, thread: ThreadConfig) => {
    setThreads((prev) => prev.map((t) => (t.id === id ? thread : t)));
  };

  const { mutateAsync } = useUpdateNode(flowId);

  /**
   * Check if multithread outputs have changed
   */
  const hasOutputsChanged = (
    existingConnections: NodeData["connections"],
    newConnections: Record<
      string,
      { display_name: string; target_node: string | null }
    >,
  ): boolean => {
    const existingKeys = existingConnections
      ? Object.keys(existingConnections)
          .filter((key) => key.startsWith("thread_"))
          .sort()
      : [];
    const newKeys = Object.keys(newConnections)
      .filter((key) => key.startsWith("thread_"))
      .sort();

    return (
      existingKeys.length !== newKeys.length ||
      existingKeys.some((key, index) => key !== newKeys[index])
    );
  };

  /**
   * Find all nodes directly connected from multithread outputs
   */
  const getMultithreadOutputNodes = (
    multithreadNodeId: string,
  ): Set<string> => {
    const outputNodes = new Set<string>();
    edges.forEach((edge) => {
      if (
        edge.source === multithreadNodeId &&
        typeof edge.sourceHandle === "string" &&
        edge.sourceHandle.startsWith("thread_")
      ) {
        outputNodes.add(edge.target);
      }
    });
    return outputNodes;
  };

  /**
   * Check if a wait node is associated with the multithread node
   */
  const isWaitNodeAssociated = (
    waitNode: { id: string; data: unknown },
    multithreadNodeId: string,
    multithreadOutputNodes: Set<string>,
  ): boolean => {
    const waitNodeData = waitNode.data as unknown as NodeData;

    // Check direct edge connection
    const hasDirectEdge = edges.some(
      (edge) =>
        edge.source === multithreadNodeId &&
        edge.target === waitNode.id &&
        typeof edge.sourceHandle === "string" &&
        edge.sourceHandle.startsWith("thread_"),
    );

    if (hasDirectEdge) return true;

    // Check inputs for connections
    if (!waitNodeData.inputs || waitNodeData.inputs.length === 0) {
      return false;
    }

    return waitNodeData.inputs.some((input) => {
      if (input.type !== "THREAD") return false;

      // Check connection field
      if (input.connection === multithreadNodeId) return true;
      if (input.connection && multithreadOutputNodes.has(input.connection))
        return true;

      // Check source_connections
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (Array.isArray((input as any).source_connections)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (input as any).source_connections.some((conn: any) => {
          return (
            conn?.source_node_id === multithreadNodeId ||
            (conn?.source_node_id &&
              multithreadOutputNodes.has(conn.source_node_id))
          );
        });
      }

      return false;
    });
  };

  /**
   * Update a wait node to match multithread outputs
   * Removes inputs for removed outputs and adds inputs for new outputs
   */
  const updateWaitNode = async (
    waitNode: { id: string; data: unknown; position: { x: number; y: number } },
    _multithreadNodeId: string,
    threadConfigs: ThreadConfig[],
  ): Promise<void> => {
    const waitNodeData = waitNode.data as unknown as NodeData;
    // Pasar también los edges para obtener el source_handler correcto
    const existingWaitInputs = deserializeWaitInputs(
      waitNodeData.inputs || [],
      waitNodeData.edges,
    );

    // Crear nuevos inputs basados en las salidas del multithread
    // Preservar conexiones existentes por posición
    const updatedWaitInputs: WaitInput[] = threadConfigs.map(
      (thread, index) => {
        const existingInput = existingWaitInputs[index];

        return {
          id: existingInput?.id,
          name: existingInput?.name || thread.label || `Entrada ${index + 1}`,
          // Preservar conexiones existentes
          sourceConnections: existingInput?.sourceConnections || [],
        };
      },
    );

    // Serializar inputs y edges
    const serializedInputs = serializeWaitInputs(
      updatedWaitInputs,
      _multithreadNodeId,
    );
    const waitNodeEdges = serializeWaitEdges(updatedWaitInputs);

    // Update wait node via API
    await mutateAsync({
      node_id: waitNode.id,
      node: {
        flow_id: flowId,
        ref_name: waitNodeData.type as RefName,
        display_name: waitNodeData.displayName,
        order: waitNodeData.order,
        position_x: waitNode.position.x,
        position_y: waitNode.position.y,
        type: waitNodeData.node_type,
        edges: waitNodeEdges.length > 0 ? waitNodeEdges : undefined,
        inputs: serializedInputs,
      },
    });

    // Update local state immediately
    const { nodes: currentNodes } = useWorkflowStore.getState();
    useWorkflowStore.setState({
      nodes: currentNodes.map((n) => {
        if (n.id === waitNode.id) {
          return {
            ...n,
            data: {
              ...(n.data as unknown as NodeData),
              inputs: serializedInputs,
            },
          };
        }
        return n;
      }),
    });
  };

  const onSaveNode = async () => {
    if (!node) return;

    setShowNodeTitleValidation(true);
    if (!isNameValid(title)) return;

    const nodeData = node.data as unknown as NodeData;

    // Build connections object from threads
    const connections = threads.reduce(
      (acc, thread, index) => {
        acc[`thread_${index + 1}`] = {
          display_name: thread.label || "",
          target_node: nodeData.connections
            ? nodeData.connections[`thread_${index + 1}`]?.target_node || null
            : null,
        };
        return acc;
      },
      {} as Record<
        string,
        { display_name: string; target_node: string | null }
      >,
    );

    // Save multithread node
    await mutateAsync({
      node_id: node.id,
      node: {
        flow_id: flowId,
        ref_name: nodeData.type as RefName,
        display_name: title,
        order: nodeData.order,
        position_x: node.position.x,
        position_y: node.position.y,
        type: nodeData.node_type,
        manual_continue: manualConfirmation,
        inputs: [],
        connections,
      },
    });

    // Update local state immediately
    const { setNodeConnections } = useWorkflowStore.getState();
    setNodeConnections(node.id, connections);

    // Only update wait nodes if outputs have changed
    if (!hasOutputsChanged(nodeData.connections, connections)) {
      setNodeDetailsOpen(false);
      setSelectedNodeId(undefined);
      return;
    }

    // Find associated wait nodes
    const multithreadOutputNodes = getMultithreadOutputNodes(node.id);
    const associatedWaitNodes = nodes.filter((workflowNode) => {
      const workflowNodeData = workflowNode.data as unknown as NodeData;
      if (workflowNodeData.type !== NodeTypes.WAIT) return false;
      return isWaitNodeAssociated(
        workflowNode,
        node.id,
        multithreadOutputNodes,
      );
    });

    // Update all associated wait nodes
    await Promise.all(
      associatedWaitNodes.map((waitNode) =>
        updateWaitNode(waitNode, node.id, threads),
      ),
    );

    setNodeDetailsOpen(false);
    setSelectedNodeId(undefined);
  };

  const areThreadsValid = useMemo(() => {
    return threads.every((t) => t.label.trim().length > 0);
  }, [threads]);

  return (
    <>
      <ConfirmationModalBase
        isOpen={modalState === "close-warning"}
        acceptText="Salir sin guardar"
        cancelText="Cancelar"
        description="¿Estás seguro de que deseas salir? Todos los cambios que hayas realizado se perderán"
        title="Salir sin guardar"
        acceptVariant="danger-secondary"
        isLoading={false}
        onAccept={() => {
          setNodeDetailsOpen(false);
          setSelectedNodeId();
          setModalState("idle");
        }}
        onClose={() => {
          setModalState("idle");
        }}
      />
      <FullModal
        isOpen={nodeDetailsOpen}
        onClose={() => {
          setModalState("close-warning");
        }}
        isSaveValid={
          areThreadsValid && threads.length >= 2 && isNameValid(title)
        }
        onSave={onSaveNode}
      >
        <ThreeColumnLayout
          leftHeader={{
            icon: "arrow-right-to-bracket",
            title: "Inputs",
            color: "neutral",
            hideSearch: true,
          }}
          centerHeader={{
            icon: "code-branch",
            title: title,
            color: "neutral",
            editable: true,
            onTitleChange: (newTitle: string) => {
              setTitle(newTitle);
              if (newTitle.trim() === "") {
                setShowNodeTitleValidation(true);
              }
            },
            onTitleBlur: () => setShowNodeTitleValidation(true),
            validationError: getNameValidationError(title),
            showValidation: showNodeTitleValidation,
            manualConfirmation,
            onManualConfirmationChange: setManualConfirmation,
          }}
          rightHeader={{
            icon: "arrow-right-from-bracket",
            title: "Outputs",
            color: "neutral",
          }}
          leftContent={
            <>
              <div className="bg-neutral-foreground overflow-auto divide-y divide-[var(--border-neutral-quiet)]">
                <Variables sourceNodeId={selectedNodeId!} />
              </div>
            </>
          }
          centerContent={
            <ThreadList
              threads={threads}
              onAddThread={onAddThread}
              onDeleteThread={onDeleteThread}
              onUpdateThread={onUpdateThread}
              maxThreads={10}
              minThreads={2}
            />
          }
          rightContent={
            <>
              <Empty />
            </>
          }
        />
      </FullModal>
    </>
  );
};

export default MultiThreadDetails;
